'use client';

import { useState } from 'react';
import YouTube from 'react-youtube';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Eye } from 'lucide-react';
import { format } from 'date-fns';
import Link from 'next/link';
import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import CommonNav from '@/components/layouts/CommonNav';
import { LogoLinks } from '@/components/common/smartguy-logo';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const formatViews = (number) => {
  const value = Number(number) || 0;
  if (value < 1000) return value.toString();
  if (value < 1_000_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (value < 1_000_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
};

export default function VideoPage({ video }) {
  const [isVideoReady, setIsVideoReady] = useState(false);

  const opts = {
    width: '100%',
    height: '100%',
    playerVars: {
      autoplay: 0,
      rel: 0,
    },
  };

  if (!video) {
    return <div>Video not found</div>;
  }

  return (
    <div className="container mx-auto max-w-7xl px-3 py-6 sm:px-4 sm:py-8">
      <div className="relative">
        <div className="space-y-4">
          <div className="flex flex-1 flex-col items-center justify-center">
            <Link href="/" className="flex flex-col items-center justify-center">
              <div className="flex items-center">
                <LogoText className="text-3xl" />
                <LogoRegister />
              </div>
              <LogoSlogan className="me-4 text-center text-[.7rem]" />
            </Link>

            <LogoLinks />
          </div>
          <div>
            <CommonNav />
          </div>
        </div>

        <div className="absolute top-2 right-0">
          <Link href="/catholics-speaks">
            <Button className="h-7 w-7 rounded-full sm:h-10 sm:w-10">
              <ArrowLeft className="size-4.5 sm:size-6" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 pt-6 lg:gap-8">
          <div className="space-y-6 lg:col-span-2">
            <Card className="overflow-hidden rounded-xl border-none bg-white py-0 shadow-lg">
              <CardContent className="p-0">
                <div className="bg-muted relative aspect-video w-full">
                  {!isVideoReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <Skeleton className="h-full w-full" />
                      <div className="text-muted-foreground absolute animate-pulse text-sm sm:text-lg">
                        Loading video...
                      </div>
                    </div>
                  )}

                  <YouTube
                    videoId={video.videoId}
                    className={`h-full w-full ${!isVideoReady ? 'invisible' : ''}`}
                    opts={opts}
                    iframeClassName="w-full h-full"
                    onReady={() => setIsVideoReady(true)}
                    onError={(e) => console.error('YouTube error:', e)}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 capitalize sm:text-3xl md:text-4xl">
                    {video.title}
                  </h1>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Eye className="size-5 text-gray-500" />
                    <span>{formatViews(video.views)} views</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="size-4 text-gray-500" />
                    <span>
                      {video.publishedAt
                        ? format(new Date(video.publishedAt), 'dd MMM, yyyy')
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {video.description && (
                <Card className="border border-gray-100 bg-white py-0">
                  <CardContent className="p-4">
                    <h3 className="mb-2 font-semibold text-gray-900">Description</h3>
                    <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700 sm:text-base">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
